import { useForm } from '@refinedev/antd';
import { Create } from '@refinedev/antd';
import { Form, Input, Checkbox, Card, Row, Col, Divider } from 'antd';
import { LabelWithHelp, SectionTitle } from '../../components/ui/HelpTooltip';
import { t } from '../../i18n';

const permissionGroups = [
  {
    title: t('staff.permissionGroups.users'),
    key: 'users',
    permissions: ['read', 'update', 'block'],
    help: "Foydalanuvchilar ro'yxati va ularni boshqarish",
  },
  {
    title: t('staff.permissionGroups.drivers'),
    key: 'drivers',
    permissions: ['read', 'update', 'verify', 'block'],
    help: "Haydovchilar ro'yxati, tasdiqlash va boshqarish",
  },
  {
    title: t('staff.permissionGroups.documents'),
    key: 'documents',
    permissions: ['read', 'review'],
    help: "Haydovchi hujjatlarini ko'rish va tasdiqlash",
  },
  {
    title: t('staff.permissionGroups.trips'),
    key: 'trips',
    permissions: ['read', 'update', 'cancel'],
    help: "Sayohatlarni ko'rish, tahrirlash va bekor qilish",
  },
  {
    title: t('staff.permissionGroups.payments'),
    key: 'payments',
    permissions: ['read'],
    help: "To'lovlar tarixini ko'rish",
  },
  {
    title: t('staff.permissionGroups.support'),
    key: 'support',
    permissions: ['read', 'reply', 'resolve'],
    help: "Foydalanuvchi murojatlarini ko'rish va javob berish",
  },
  {
    title: t('staff.permissionGroups.faq'),
    key: 'faq',
    permissions: ['read', 'create', 'update', 'delete'],
    help: "Ko'p so'raladigan savollarni boshqarish",
  },
  {
    title: t('staff.permissionGroups.promo'),
    key: 'promo',
    permissions: ['read', 'create', 'update', 'delete'],
    help: "Chegirma kodlarini yaratish va boshqarish",
  },
  {
    title: t('staff.permissionGroups.settings'),
    key: 'settings',
    permissions: ['read', 'update'],
    help: "Tizim sozlamalarini o'zgartirish",
  },
  {
    title: t('staff.permissionGroups.staff'),
    key: 'staff',
    permissions: ['read', 'create', 'update', 'delete'],
    help: "Admin xodimlarni yaratish va boshqarish",
  },
];

const permissionLabels: Record<string, string> = {
  read: t('staff.actions.read'),
  create: t('staff.actions.create'),
  update: t('staff.actions.update'),
  delete: t('staff.actions.delete'),
  block: t('staff.actions.block'),
  verify: t('staff.actions.verify'),
  review: t('staff.actions.review'),
  reply: t('staff.actions.reply'),
  resolve: t('staff.actions.resolve'),
  cancel: t('staff.actions.cancel'),
};

export const StaffCreate = () => {
  const { formProps, saveButtonProps } = useForm({
    resource: 'admin/staff',
    redirect: 'list',
  });

  // Clean up permissions before submission: remove empty arrays and convert to proper format
  const handleFinish = (values: Record<string, unknown>) => {
    const cleaned = { ...values };
    if (cleaned.permissions && typeof cleaned.permissions === 'object') {
      const perms = cleaned.permissions as Record<string, string[]>;
      const filteredPerms: Record<string, string[]> = {};
      for (const [key, val] of Object.entries(perms)) {
        if (Array.isArray(val) && val.length > 0) {
          filteredPerms[key] = val;
        }
      }
      cleaned.permissions = Object.keys(filteredPerms).length > 0 ? filteredPerms : undefined;
    }
    return formProps.onFinish?.(cleaned);
  };

  return (
    <Create saveButtonProps={saveButtonProps} title={t('staff.newStaff')}>
      <Form {...formProps} onFinish={handleFinish} layout="vertical">
        <Row gutter={24}>
          <Col xs={24} lg={12}>
            <Card
              title={
                <SectionTitle
                  title={t('staff.basicInfo')}
                  help="Xodimning kirish ma'lumotlari va shaxsiy ma'lumotlari"
                />
              }
            >
              <Form.Item
                label={
                  <LabelWithHelp
                    label={t('staff.login')}
                    help="Xodim admin panelga kirish uchun foydalanadigan login. Kamida 3 ta belgi."
                  />
                }
                name="username"
                rules={[
                  { required: true, message: 'Loginni kiriting' },
                  { min: 3, message: "Kamida 3 ta belgi bo'lishi kerak" },
                ]}
              >
                <Input placeholder="admin" />
              </Form.Item>

              <Form.Item
                label={
                  <LabelWithHelp
                    label={t('staff.password')}
                    help="Xodim uchun parol. Kamida 6 ta belgi. Xavfsiz parol tanlang."
                  />
                }
                name="password"
                rules={[
                  { required: true, message: 'Parolni kiriting' },
                  { min: 6, message: "Kamida 6 ta belgi bo'lishi kerak" },
                ]}
              >
                <Input.Password placeholder="Kamida 6 ta belgi" />
              </Form.Item>

              <Form.Item
                label={
                  <LabelWithHelp
                    label={t('staff.fullName')}
                    help="Xodimning to'liq ismi va familiyasi"
                  />
                }
                name="fullName"
              >
                <Input placeholder="Abdullayev Abdulla" />
              </Form.Item>

              <Form.Item
                label={
                  <LabelWithHelp
                    label={t('staff.phone')}
                    help="Xodimning telefon raqami. Aloqa uchun ishlatiladi."
                  />
                }
                name="phone"
              >
                <Input placeholder="+998901234567" />
              </Form.Item>
            </Card>
          </Col>

          <Col xs={24} lg={12}>
            <Card
              title={
                <SectionTitle
                  title={t('staff.permissions')}
                  help="Xodimga qaysi bo'limlarga kirish va qanday amallar bajarishga ruxsat beriladi"
                />
              }
            >
              {permissionGroups.map((group) => (
                <div key={group.key} style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 500, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <LabelWithHelp label={group.title} help={group.help} />
                  </div>
                  <Form.Item name={['permissions', group.key]} noStyle>
                    <Checkbox.Group>
                      {group.permissions.map((perm) => (
                        <Checkbox key={perm} value={perm}>
                          {permissionLabels[perm] || perm}
                        </Checkbox>
                      ))}
                    </Checkbox.Group>
                  </Form.Item>
                  <Divider style={{ margin: '12px 0' }} />
                </div>
              ))}
            </Card>
          </Col>
        </Row>
      </Form>
    </Create>
  );
};
